// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Typography } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Tooltip, { TooltipPresentationProps } from "@sc/MobiusOverrides/Tooltip";
import TooltipIcon from "@sc/XDL/Icons/TooltipIcon";
import { mergeWithCss, xClassNames, xMask } from "@sc/XDL/Utilities";
import xDefaultRules from "@sc/XDL/Utilities/xValidate/xDefaultRules";
import React, { FC, useEffect, useState } from "react";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    min?: number | string;
    max?: number | string;
    minimumOrderQty?: number;
    multipleSaleQty?: number;
    showMultipleSaleQtyTooltip?: boolean;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    countButton?: ButtonPresentationProps;
    increaseButton?: ButtonPresentationProps;
    decreaseButton?: ButtonPresentationProps;
    countIcon?: IconPresentationProps;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
    printQtyLabel?: TypographyProps;
    printValueText?: TypographyProps;
    qtyTooltipWrapper?: InjectableCss;
    multipleSaleQtyWrapper?: InjectableCss;
    multipleSaleQtyToolTip?: TooltipPresentationProps;
    tooltipBody?: InjectableCss;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    questionIconStyles?: IconProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField: FC<Props> = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    allowZero = false,
    editable = true,
    showLabel = true,
    // Xng-Changes
    min = 0,
    max = 9999,
    minimumOrderQty,
    multipleSaleQty,
    showMultipleSaleQtyTooltip,
    ...qtyFieldsProps
}) => {
    const [styles] = useState(() => mergeWithCss(qtyFieldStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    // Xng-Changes
    let minQty = 0;
    let maxQty = 9999;
    xDefaultRules["qty"].split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = Math.min(Number(max), +val);
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Xng-Changes
        event.target.value = xMask.qty(event.target.value);
        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.currentTarget) {
            // Xng-Changes
            event.currentTarget.value = xMask.qty(event.currentTarget.value);
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - multipleSaleQty >= minQty
                    ? Number(currentValue) - multipleSaleQty
                    : multipleSaleQty;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - 1 >= minQty
                    ? Number(currentValue) - 1
                    : undefined;
        }

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }
        // Xng-Changes
        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }
        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + multipleSaleQty <= maxQty
                    ? Number(currentValue) + multipleSaleQty
                    : undefined;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + 1 <= maxQty
                    ? Number(currentValue) + 1
                    : undefined;
        }

        if (!nextValue) {
            return;
        }
        // Xng-Changes
        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const multipleSaleTooltip = showMultipleSaleQtyTooltip && (
        <StyledWrapper {...styles.multipleSaleQtyWrapper}>
            <Tooltip
                text={
                    <StyledWrapper {...styles.tooltipBody}>
                        {!!minimumOrderQty && (
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Minimum Order Qty")}
                                text={minimumOrderQty}
                            />
                        )}
                        {!!multipleSaleQty && (
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Multiple Sale Qty")}
                                text={multipleSaleQty}
                            />
                        )}
                    </StyledWrapper>
                }
                triggerComponent={<Icon {...styles.questionIconStyles} src={TooltipIcon} />}
                {...styles.multipleSaleQtyToolTip}
            />
        </StyledWrapper>
    );

    return (
        <>
            {editable ? (
                <>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    {/* Xng-Change: add print label and value */}
                    <Typography {...styles.printQtyLabel}>{labelOverride || translate("QTY")}</Typography>
                    <Typography {...styles.printValueText}>{currentValue}</Typography>
                    <StyledWrapper {...styles.qtyTooltipWrapper} className="editableQtyTooltip">
                        <StyledWrapper
                            {...styles.wrapper}
                            data-test-selector="quantity-field"
                            className={xClassNames.append([
                                disabled && "qty-disabled",
                                showActionButton && "show-action-button",
                            ])}
                        >
                            {showActionButton && (
                                <Button
                                    {...styles.decreaseButton}
                                    disabled={disabled || Number(currentValue) === minQty || Number(currentValue) === 0}
                                    onClick={onDecreaseClickHandler}
                                    aria-label="qty-decrement"
                                >
                                    <Icon {...styles.countIcon} src={Minus} />
                                </Button>
                            )}
                            <TextField
                                type="qty"
                                disabled={disabled}
                                value={currentValue}
                                onChange={onChangeHandler}
                                onBlur={onBlurHandler}
                                onClick={onClickHandler}
                                onKeyDown={onKeyDownHandler}
                                label={labelOverride || translate("QTY")}
                                {...styles.editableQuantityTextField}
                                {...qtyFieldsProps}
                            />
                            {showActionButton && (
                                <Button
                                    {...styles.increaseButton}
                                    disabled={disabled || Number(currentValue) === maxQty}
                                    onClick={onIncreaseClickHandler}
                                    aria-label="qty-increment"
                                >
                                    <Icon {...styles.countIcon} src={Plus} />
                                </Button>
                            )}
                        </StyledWrapper>
                        {/* Add multiple sale qty tooltip */}
                        {multipleSaleTooltip}
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    <Typography {...styles.printQtyLabel}>{labelOverride || translate("QTY")}</Typography>
                    <StyledWrapper {...styles.qtyTooltipWrapper}>
                        <Typography {...styles.valueText}>{currentValue}</Typography>
                        {/* Add multiple sale qty tooltip */}
                        {multipleSaleTooltip}
                    </StyledWrapper>
                </>
            )}
        </>
    );
};

export default QtyField;
